/**
 * Sales-channel setup submit policy (TV1-1904, rule-based rework).
 *
 * Reactions, each keyed on a loading flag's false→true transition:
 *
 *   Rule creation (channel-scoped automation rules via `SetRule`):
 *   - `savingInventory`  → SetRule( fixed-warehouse [-on-status] )
 *                          + SetRule( channel-stock-formula-update ) — the paired
 *                            rule that republishes the chosen warehouse's stock to
 *                            the channel listing on every warehouse-stock change.
 *   - `savingLogistics`  → SetRule( fixed-logistics-provider [-on-status] )
 *
 *   Channel association (via `UpdateSystemIntegrationConfiguration`):
 *   - `savingBilling`    → write `billingEntityId`
 *   - `completing`       → write `settings.channelSetupCompletedAt`
 *
 *   Inline creation (reuses the canonical provisioning / billing commands):
 *   - `provisioningInvoicing` → ProvisionSystemIntegration / `authorize` webhook
 *   - `creatingBilling`       → AddBillingEntity
 *   - `registeringLogistics`  → ProvisionSystemIntegration / `authorize` webhook
 *
 * `SetRule` is an upsert keyed by `ruleId`: the first save omits it (create) and
 * captures the returned id; subsequent saves pass it (update in place).
 */

import { uuidv7 } from 'uuidv7';
import { Policy } from '@commandDrivenStateEngine/core/policies';
import { ClientLibraryType } from '@bett3r-dev/teselly-client-library';
import {
  SalesChannelSetupState,
  selectors,
  WAREHOUSE_RULE_DEFINITION_IDS,
  LOGISTICS_RULE_DEFINITION_IDS,
  BILLING_RULE_DEFINITION_IDS,
  CHANNEL_STOCK_RULE_DEFINITION_ID,
  CHANNEL_STOCK_TRIGGERING_EVENTS,
  buildChannelStockFormula
} from '../state';
import { SalesChannelSetupCommand } from '../commands';
import { ConnectorEntry } from '../../../shared/state/state';
import {
  resolveSetupPanelKind,
  canSubmitApiKey,
  canDirectProvision,
  canImportOrderHistory
} from '../../../shared/state/capabilityGates';

type Ctx = {
  dispatch: ( cmd: SalesChannelSetupCommand, meta: { source: string }) => void;
};

const extractRuleId = ( result: any, fallback: string | null ): string | null =>
  result?.events?.find(( e: any ) => e.name === 'RuleSet' )?.data?.ruleId ?? fallback;

export const createSetupSubmitPolicy = (
  backend: ClientLibraryType,
  getConnectors: () => Record<string, ConnectorEntry>
): Policy<SalesChannelSetupState, SalesChannelSetupCommand> => {
  const channelHeader = ( next: SalesChannelSetupState ) => ({
    id: next.data.integrationId as string,
    accountId: next.data.accountId as string,
    connectorId: next.data.connectorId as string,
    integrationUserId: next.data.integrationUserId as string
  });

  const hasChannel = ( next: SalesChannelSetupState ): boolean =>
    !!next.data.integrationId &&
    !!next.data.accountId &&
    !!next.data.connectorId &&
    !!next.data.integrationUserId;

  // ───────────────────────────────────────────────────────────────────────────
  // Rule creation (SetRule)
  // ───────────────────────────────────────────────────────────────────────────

  const submitInventory = async ( next: SalesChannelSetupState, ctx: Ctx ): Promise<void> => {
    const { warehouseId, trigger, statusId, ruleId, channelStockRuleId } = next.inventory;
    if ( !hasChannel( next ) || !warehouseId ) {
      ctx.dispatch(
        { type: 'salesChannelSetup/inventory/submit/error', payload: {}},
        { source: 'api' }
      );
      return;
    }
    const declaredInputsValues: Record<string, unknown> = {
      warehouse: { source: 'warehouses', warehouseId }
    };
    if ( trigger === 'OrderStatusChanged' && statusId ) {
      declaredInputsValues.desiredStatus = { source: 'statuses', statusId };
    }
    const warehouseBody = {
      ...( ruleId ? { ruleId } : {}),
      ruleDefinitionId: WAREHOUSE_RULE_DEFINITION_IDS[trigger],
      hierarchyLevel: 'channel',
      hierarchyEntityIds: [next.data.integrationId as string],
      triggeringEvents: [trigger],
      categories: ['warehouseStock'],
      enabled: true,
      declaredInputsValues
    };
    // Paired channel-stock rule: republishes the chosen warehouse's stock to the
    // channel listing on every warehouse-stock change, so an order → warehouse
    // decrement propagates to the channel. It is warehouse-stock-triggered (not
    // order-triggered), hence a single definition regardless of the step's trigger;
    // its formula is derived from the same fixed warehouse.
    const channelStockBody = {
      ...( channelStockRuleId ? { ruleId: channelStockRuleId } : {}),
      ruleDefinitionId: CHANNEL_STOCK_RULE_DEFINITION_ID,
      hierarchyLevel: 'channel',
      hierarchyEntityIds: [next.data.integrationId as string],
      triggeringEvents: CHANNEL_STOCK_TRIGGERING_EVENTS,
      categories: ['publicationStock'],
      enabled: true,
      declaredInputsValues: {
        formula: buildChannelStockFormula( warehouseId ),
        stockOperation: 'sum'
      }
    };
    try {
      const warehouseResult = await backend.commands.SetRule({
        id: next.data.integrationId as string,
        body: warehouseBody as any
      });
      const channelStockResult = await backend.commands.SetRule({
        id: next.data.integrationId as string,
        body: channelStockBody as any
      });
      ctx.dispatch(
        {
          type: 'salesChannelSetup/inventory/submit/success',
          payload: {
            ruleId: extractRuleId( warehouseResult, ruleId ),
            channelStockRuleId: extractRuleId( channelStockResult, channelStockRuleId )
          }
        },
        { source: 'api' }
      );
    } catch ( error ) {
      console.error( '[salesChannelSetup] Inventory rule submit failed:', error );
      ctx.dispatch(
        { type: 'salesChannelSetup/inventory/submit/error', payload: {}},
        { source: 'api' }
      );
      throw error;
    }
  };

  const submitLogistics = async ( next: SalesChannelSetupState, ctx: Ctx ): Promise<void> => {
    const { logisticsIntegrationId, trigger, statusId, ruleId } = next.logistics;
    const option = next.catalogs.logisticsIntegrations.find(
      ( i ) => i.id === logisticsIntegrationId
    );
    if ( !hasChannel( next ) || !logisticsIntegrationId || !option ) {
      ctx.dispatch(
        { type: 'salesChannelSetup/logistics/submit/error', payload: {}},
        { source: 'api' }
      );
      return;
    }
    const declaredInputsValues: Record<string, unknown> = {
      logisticsProvider: {
        source: 'logisticsProviders',
        connectorId: option.connectorId,
        integrationUserId: option.integrationUserId
      }
    };
    if ( trigger === 'OrderStatusChanged' && statusId ) {
      declaredInputsValues.desiredStatus = { source: 'statuses', statusId };
    }
    const body = {
      ...( ruleId ? { ruleId } : {}),
      ruleDefinitionId: LOGISTICS_RULE_DEFINITION_IDS[trigger],
      hierarchyLevel: 'channel',
      hierarchyEntityIds: [next.data.integrationId as string],
      triggeringEvents: [trigger],
      categories: ['shipments'],
      enabled: true,
      declaredInputsValues
    };
    try {
      const result = await backend.commands.SetRule({
        id: next.data.integrationId as string,
        body: body as any
      });
      ctx.dispatch(
        {
          type: 'salesChannelSetup/logistics/submit/success',
          payload: { ruleId: extractRuleId( result, ruleId ) }
        },
        { source: 'api' }
      );
    } catch ( error ) {
      console.error( '[salesChannelSetup] Logistics rule submit failed:', error );
      ctx.dispatch(
        { type: 'salesChannelSetup/logistics/submit/error', payload: {}},
        { source: 'api' }
      );
      throw error;
    }
  };

  // ───────────────────────────────────────────────────────────────────────────
  // Channel association
  // ───────────────────────────────────────────────────────────────────────────

  const submitBilling = async ( next: SalesChannelSetupState, ctx: Ctx ): Promise<void> => {
    const billingEntityId = next.billing.billingEntityId;
    if ( !hasChannel( next ) || !billingEntityId ) {
      ctx.dispatch(
        { type: 'salesChannelSetup/billing/submit/error', payload: {}},
        { source: 'api' }
      );
      return;
    }
    try {
      // 1. Keep the billing-entity association on the channel.
      await backend.commands.UpdateSystemIntegrationConfiguration({
        id: next.data.integrationId as string,
        body: { ...channelHeader( next ), billingEntityId } as any
      });

      // 2. Create/update the invoice-generation rule. OrderPlaced uses the
      //    no-status invoice rule (fires on placement); OrderStatusChanged uses the
      //    status-gated rule and therefore needs a chosen status.
      let ruleId = next.billing.ruleId;
      const { trigger, statusId } = next.billing;
      const shouldCreateRule = trigger === 'OrderPlaced' || !!statusId;
      if ( shouldCreateRule ) {
        const declaredInputsValues: Record<string, unknown> = {
          billingEntityId: { source: 'billingEntities', billingEntityId }
        };
        if ( trigger === 'OrderStatusChanged' && statusId ) {
          declaredInputsValues.value1 = { source: 'statuses', statusId };
        }
        const body = {
          ...( ruleId ? { ruleId } : {}),
          ruleDefinitionId: BILLING_RULE_DEFINITION_IDS[trigger],
          hierarchyLevel: 'channel',
          hierarchyEntityIds: [next.data.integrationId as string],
          triggeringEvents: [trigger],
          categories: ['invoicing'],
          enabled: true,
          declaredInputsValues
        };
        const result = await backend.commands.SetRule({
          id: next.data.integrationId as string,
          body: body as any
        });
        ruleId = extractRuleId( result, ruleId );
      }

      ctx.dispatch(
        { type: 'salesChannelSetup/billing/submit/success', payload: { ruleId }},
        { source: 'api' }
      );
    } catch ( error ) {
      console.error( '[salesChannelSetup] Billing submit failed:', error );
      ctx.dispatch(
        { type: 'salesChannelSetup/billing/submit/error', payload: {}},
        { source: 'api' }
      );
      throw error;
    }
  };

  const submitComplete = async ( next: SalesChannelSetupState, ctx: Ctx ): Promise<void> => {
    if ( !hasChannel( next )) {
      ctx.dispatch(
        { type: 'salesChannelSetup/complete/error', payload: {}},
        { source: 'api' }
      );
      return;
    }
    const settings = {
      ...next.data.currentSettings,
      channelSetupCompletedAt: new Date().toISOString()
    };
    try {
      await backend.commands.UpdateSystemIntegrationConfiguration({
        id: next.data.integrationId as string,
        body: { settings } as any
      });

      /*
       * TV1-2354 — one-shot publication import, if the operator asked for it.
       *
       * AFTER the completion marker is written, and deliberately NOT inside the
       * try that guards it: the import is an independent, retryable batch process,
       * and a channel whose setup succeeded must not be reported as failed because
       * a catalog pull could not be kicked off. A failure here surfaces as a failed
       * batch process, which is where the operator can retry it.
       *
       * No `channelPublicationIds` — that means "import everything", the same
       * shape `ChannelInfo`'s import modal sends for its `all` mode.
       */
      if ( next.publications.autoImport && next.data.connectorId && next.data.integrationUserId ) {
        // eslint-disable-next-line teselly/no-swallowed-command-dispatch -- operator log only; the failed run is visible as a batch process, and setup itself succeeded (TV1-2354).
        try {
          await backend.commands.StartChannelPublicationImportBatchProcess({
            id: uuidv7(),
            body: {
              connectorId: next.data.connectorId,
              integrationUserId: next.data.integrationUserId
            }
          } as any );
        } catch ( error ) {
          console.error( '[salesChannelSetup] Auto-import of publications failed to start:', error );
        }
      }

      /*
       * TV1-2379 — one-shot import of the channel's FULL past order history, if
       * the operator asked for it.
       *
       * Its OWN inner try/catch, sibling to the publications one rather than
       * sharing it: these are two independent batch processes with different
       * failure modes, and a publication import that started fine must not be
       * reported as failed — nor prevented — because the order seed could not
       * start, or the reverse.
       *
       * `canImportOrderHistory` re-checks here even though the switch is hidden
       * for unsupported connectors: the flag is wizard-local state that can
       * outlive a connector change within one session, and the aggregate rejects
       * an unsupported connector with
       * BATCH_PROCESSES_CHANNEL_CAPABILITY_UNSUPPORTED.
       *
       * No date window — seeding pulls the whole history, by design.
       */
      if (
        next.publications.autoImportOrders &&
        next.data.connectorId &&
        next.data.integrationUserId &&
        canImportOrderHistory( next.data.connectorId )
      ) {
        // eslint-disable-next-line teselly/no-swallowed-command-dispatch -- operator log only; the failed run is visible as a batch process, and setup itself succeeded (TV1-2379).
        try {
          // TEMPORARY CAST (TV1-2379) — `StartChannelOrderImportBatchProcess` is
          // not yet on the generated `ClientLibraryType`, so the PROPERTY ACCESS
          // itself does not typecheck. Remove `as any` after
          // `yarn build && yarn generate-all`.
          await ( backend.commands as any ).StartChannelOrderImportBatchProcess({
            id: uuidv7(),
            body: {
              connectorId: next.data.connectorId,
              integrationUserId: next.data.integrationUserId
            }
          });
        } catch ( error ) {
          console.error( '[salesChannelSetup] Auto-import of order history failed to start:', error );
        }
      }

      ctx.dispatch(
        { type: 'salesChannelSetup/complete/success', payload: {}},
        { source: 'api' }
      );
    } catch ( error ) {
      console.error( '[salesChannelSetup] Complete failed:', error );
      ctx.dispatch(
        { type: 'salesChannelSetup/complete/error', payload: {}},
        { source: 'api' }
      );
      throw error;
    }
  };

  // ───────────────────────────────────────────────────────────────────────────
  // Inline creation
  // ───────────────────────────────────────────────────────────────────────────

  const provisionInvoicing = async ( next: SalesChannelSetupState, ctx: Ctx ): Promise<void> => {
    const connectorId = next.invoicing.selectedConnectorId;
    const credentials = next.invoicing.credentials;

    const fail = () =>
      ctx.dispatch(
        { type: 'salesChannelSetup/invoicing/provision/error', payload: {}},
        { source: 'api' }
      );

    if ( !connectorId ) {
      console.error( '[salesChannelSetup] No invoicing connector selected' );
      fail();
      return;
    }

    const connector = getConnectors()[connectorId];
    const panelKind = resolveSetupPanelKind( connector );

    try {
      if ( panelKind === 'directProvision' ) {
        if ( !canDirectProvision( connector )) {
          throw new Error( `Connector ${connectorId} does not declare the direct-provision capability` );
        }
        const integrationUserId = uuidv7();
        const integrationId = `${connectorId}_${integrationUserId}`;
        await backend.commands.ProvisionSystemIntegration({
          id: integrationId,
          body: { connectorId, integrationUserId } as any
        });
        ctx.dispatch(
          { type: 'salesChannelSetup/invoicing/provision/success', payload: { integrationId }},
          { source: 'api' }
        );
        return;
      }

      if ( panelKind === 'apiKey' ) {
        if ( !canSubmitApiKey( connector )) {
          throw new Error( `Connector ${connectorId} does not declare the api-key capability` );
        }
        await backend.post.systems_integrations_webhooks_post(
          { connectorId, route: 'authorize' },
          { ...credentials }
        );
        const integrationId = `${connectorId}_${uuidv7()}`;
        ctx.dispatch(
          { type: 'salesChannelSetup/invoicing/provision/success', payload: { integrationId }},
          { source: 'api' }
        );
        return;
      }

      console.warn(
        `[salesChannelSetup] Invoicing provision on '${connectorId}' with unsupported panel kind '${panelKind}'; ignoring.`
      );
      fail();
    } catch ( error ) {
      console.error( '[salesChannelSetup] Invoicing provision failed:', error );
      fail();
      throw error;
    }
  };

  const createBilling = async ( next: SalesChannelSetupState, ctx: Ctx ): Promise<void> => {
    const { form } = next.billing;

    // TV1-2203 — `vatCondition` is the SELLER (emisor) regime and it DECIDES the
    // comprobante class. Left unset it lands on the derivation's
    // `undefined -> responsableInscripto` fallback — the one branch in the whole
    // matrix that GUESSES, on an irreversible fiscal document. Mantine's `required`
    // only renders an asterisk and sets `aria-required`; these forms submit through
    // an `onClick` handler, not a native <form> submit, so nothing was actually
    // blocked. This is the real gate. It is checked HERE rather than folded into
    // the `canSubmit`/valid selector so the user gets a rendered field error instead
    // of a silently disabled button.
    const vatConditionMissing = !form.vatCondition;

    if ( !selectors.selectCanCreateBilling( next ) || vatConditionMissing ) {
      const errors: Record<string, string> = {};
      if ( !form.name.trim()) errors.name = 'Nombre comercial es requerido';
      if ( !form.legalName.trim()) errors.legalName = 'Razón social es requerida';
      if ( !form.documentId.trim()) errors.documentId = 'CUIT es requerido';
      // `vatCondition` is a SELECT value, not free text — it is `VatCondition |
      // undefined`, so test falsiness; `.trim()` would throw on the unset case.
      if ( vatConditionMissing ) errors.vatCondition = 'Condición IVA es requerida';
      if ( !form.billingAddress.trim()) errors.billingAddress = 'Dirección fiscal es requerida';
      if ( !form.invoicingIntegrationId.trim()) {
        errors.invoicingIntegrationId = 'La integración de facturación es requerida';
      }
      ctx.dispatch(
        { type: 'salesChannelSetup/billing/setFormErrors', payload: { errors }},
        { source: 'api' }
      );
      ctx.dispatch(
        { type: 'salesChannelSetup/billing/create/error', payload: {}},
        { source: 'api' }
      );
      return;
    }

    try {
      const billingEntityId = uuidv7();
      await backend.commands.AddBillingEntity({
        id: billingEntityId,
        body: {
          name: form.name.trim(),
          legalName: form.legalName.trim(),
          documentId: form.documentId.trim(),
          billingAddress: form.billingAddress.trim(),
          vatCondition: form.vatCondition,
          grossIncome: form.grossIncome?.trim(),
          activityStartDate: form.activityStartDate,
          // TV1-2210 (S6) — `BillingEntityFormData.pointsOfSale` IS ARCA's structured
          // record now (shared with the account-domain form), so this is a
          // passthrough. S4's `.map( p => ({ number: Number( p ) }) )` discarded
          // `emisionTipo` / `bloqueado` / `fchBaja`.
          pointsOfSale: form.pointsOfSale || [],
          taxAgency: form.taxAgency as 'arca',
          invoicingIntegrationId:
            next.invoicing.provisionedIntegrationId ?? form.invoicingIntegrationId.trim()
        }
      });
      ctx.dispatch(
        {
          type: 'salesChannelSetup/billing/create/success',
          payload: {
            billingEntityId,
            // Display name for the optimistic catalog append (mirrors the init-load
            // mapping `e.name ?? e.legalName ?? e.id`), so the new entity is
            // immediately selectable without waiting for the readmodel to catch up.
            name: form.name.trim() || form.legalName.trim() || billingEntityId
          }
        },
        { source: 'api' }
      );
    } catch ( error ) {
      console.error( '[salesChannelSetup] Billing create failed:', error );
      ctx.dispatch(
        { type: 'salesChannelSetup/billing/create/error', payload: {}},
        { source: 'api' }
      );
      throw error;
    }
  };

  const registerLogistics = async ( next: SalesChannelSetupState, ctx: Ctx ): Promise<void> => {
    const { selectedConnectorId: connectorId, credentials, instanceName } = next.logistics.create;
    const label = instanceName.trim() || undefined;

    const fail = () =>
      ctx.dispatch(
        { type: 'salesChannelSetup/logistics/register/error', payload: {}},
        { source: 'api' }
      );

    if ( !connectorId ) {
      console.error( '[salesChannelSetup] No logistics connector selected' );
      fail();
      return;
    }

    const connector = getConnectors()[connectorId];
    const panelKind = resolveSetupPanelKind( connector );

    try {
      if ( panelKind === 'apiKey' ) {
        if ( !canSubmitApiKey( connector )) {
          throw new Error( `Connector ${connectorId} does not declare the api-key capability` );
        }
        await backend.post.systems_integrations_webhooks_post(
          { connectorId, route: 'authorize' },
          { ...credentials, ...( label ? { label } : {}) }
        );
        ctx.dispatch(
          { type: 'salesChannelSetup/logistics/register/success', payload: { integrationId: null }},
          { source: 'api' }
        );
        return;
      }

      if ( panelKind === 'directProvision' ) {
        if ( !canDirectProvision( connector )) {
          throw new Error( `Connector ${connectorId} does not declare the direct-provision capability` );
        }
        const integrationUserId = uuidv7();
        const integrationId = `${connectorId}_${integrationUserId}`;
        await backend.commands.ProvisionSystemIntegration({
          id: integrationId,
          body: { connectorId, integrationUserId, ...( label ? { label } : {}) } as any
        });
        ctx.dispatch(
          { type: 'salesChannelSetup/logistics/register/success', payload: { integrationId }},
          { source: 'api' }
        );
        return;
      }

      console.warn(
        `[salesChannelSetup] Logistics register on '${connectorId}' with unsupported panel kind '${panelKind}'; ignoring.`
      );
      fail();
    } catch ( error ) {
      console.error( '[salesChannelSetup] Logistics register failed:', error );
      fail();
      throw error;
    }
  };

  return {
    name: 'sales-channel-setup-submit',

    shouldReact: ( prev, next ) =>
      ( !prev.loading.savingInventory && next.loading.savingInventory ) ||
      ( !prev.loading.savingBilling && next.loading.savingBilling ) ||
      ( !prev.loading.savingLogistics && next.loading.savingLogistics ) ||
      ( !prev.loading.completing && next.loading.completing ) ||
      ( !prev.loading.provisioningInvoicing && next.loading.provisioningInvoicing ) ||
      ( !prev.loading.creatingBilling && next.loading.creatingBilling ) ||
      ( !prev.loading.registeringLogistics && next.loading.registeringLogistics ),

    onStateChange: async ({ prev, next, ctx }) => {
      if ( !prev.loading.savingInventory && next.loading.savingInventory ) {
        await submitInventory( next, ctx );
      }
      if ( !prev.loading.savingBilling && next.loading.savingBilling ) {
        await submitBilling( next, ctx );
      }
      if ( !prev.loading.savingLogistics && next.loading.savingLogistics ) {
        await submitLogistics( next, ctx );
      }
      if ( !prev.loading.completing && next.loading.completing ) {
        await submitComplete( next, ctx );
      }
      if ( !prev.loading.provisioningInvoicing && next.loading.provisioningInvoicing ) {
        await provisionInvoicing( next, ctx );
      }
      if ( !prev.loading.creatingBilling && next.loading.creatingBilling ) {
        await createBilling( next, ctx );
      }
      if ( !prev.loading.registeringLogistics && next.loading.registeringLogistics ) {
        await registerLogistics( next, ctx );
      }
    }
  };
};
